import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Input,
  Row,
  Spinner,
} from 'reactstrap';
import Select from 'react-select';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import {
  fetchGlobalAssets,
  fetchGlobalSubOrganizations,
} from '../../api/global';
import { refreshAggregateViewOrgs } from '../../store/activeContext/actions';

const getAssetStatusBadgeClass = (status) => {
  switch (status) {
    case 'ONLINE':
      return 'bg-success';
    case 'OFFLINE':
      return 'bg-danger';
    case 'DECOMMISSIONED':
      return 'bg-dark';
    default:
      return 'bg-secondary';
  }
};

const GlobalAssets = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  document.title = `${t('shared.assets', 'Assets')} | Arcmedix`;

  const activeContext = useSelector((s) => s.ActiveContext);
  const aggregateOrgCount = activeContext?.aggregateOrgCount || 0;
  const totalMembershipCount = Array.isArray(activeContext?.memberships)
    ? activeContext.memberships.length
    : 0;

  const [subOrgs, setSubOrgs] = useState([]);
  const [orgFilter, setOrgFilter] = useState([]); // array of { value, label }
  const [search, setSearch] = useState('');
  const [count, setCount] = useState(20);
  const [assets, setAssets] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadSubOrgs = useCallback(async () => {
    try {
      const { data } = await fetchGlobalSubOrganizations();
      const liveSubOrgs = Array.isArray(data?.response) ? data.response : [];
      setSubOrgs(liveSubOrgs);
      // Reconcile sidebar's hasAggregateView flags with the live permission set
      // — fixes the stale "(N orgs)" count after a role change.
      dispatch(refreshAggregateViewOrgs(liveSubOrgs.map((o) => o.id)));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load organizations');
    }
  }, [dispatch]);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGlobalAssets({
        subOrgIds: orgFilter.map((o) => o.value),
        search,
        count,
      });
      const payload = data?.response;
      setAssets(payload?.assets || []);
      setTotal(payload?.total || 0);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [orgFilter, search, count]);

  useEffect(() => {
    loadSubOrgs();
  }, [loadSubOrgs]);

  useEffect(() => {
    loadAssets();
  }, [loadAssets]);

  const orgOptions = useMemo(
    () => subOrgs.map((org) => ({ value: org.id, label: org.name })),
    [subOrgs]
  );

  const handleLoadMore = () => setCount((c) => c + 20);
  const hasMore = assets.length < total;

  return (
    <div className="page-content">
      <div className="container-fluid">
        {aggregateOrgCount > 0 && (
          <Alert color="info" className="mb-3">
            {t(
              'shared.global_view.scope_label',
              'Aggregated across {{count}} of your {{total}} organizations',
              { count: aggregateOrgCount, total: totalMembershipCount }
            )}
          </Alert>
        )}

        <Card>
          <CardHeader>
            <Row className="g-2 align-items-center">
              <Col md={5}>
                <Input
                  type="search"
                  placeholder={t(
                    'shared.global_org.search_placeholder',
                    'Search assets by name, number, or manufacturer…'
                  )}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Col>
              <Col md={5}>
                <Select
                  isMulti
                  isClearable
                  placeholder={t(
                    'shared.global_org.org_filter',
                    'Filter by organization'
                  )}
                  options={orgOptions}
                  value={orgFilter}
                  onChange={(v) => setOrgFilter(v || [])}
                  menuPortalTarget={document.body}
                  styles={{
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: '#3980c0',
                      borderRadius: '4px',
                    }),
                    multiValueLabel: (base) => ({
                      ...base,
                      color: '#fff',
                      padding: '2px 6px',
                    }),
                    multiValueRemove: (base) => ({
                      ...base,
                      color: '#fff',
                      ':hover': {
                        backgroundColor: '#2e669a',
                        color: '#fff',
                      },
                    }),
                  }}
                />
              </Col>
              <Col md={2} className="text-end">
                <small className="text-muted">
                  {assets.length} / {total}
                </small>
              </Col>
            </Row>
          </CardHeader>

          <CardBody>
            {error && <Alert color="danger">{error}</Alert>}

            <div
              className="table-responsive"
              style={{
                overflowX: 'auto',
                whiteSpace: 'nowrap',
                maxHeight: 'calc(100vh - 300px)',
                overflowY: 'auto',
              }}
            >
              <table
                className="table table-hover mb-0"
                style={{ borderSpacing: '0.1rem', borderCollapse: 'separate' }}
              >
                <thead
                  style={{
                    position: 'sticky',
                    top: 0,
                    background: 'white',
                    zIndex: 1,
                  }}
                >
                  <tr>
                    <th>#</th>
                    <th>{t('shared.name', 'Name')}</th>
                    <th>{t('shared.asset_id', 'Asset ID')}</th>
                    <th>{t('shared.asset_status', 'Asset Status')}</th>
                    <th>{t('shared.global_org.organization', 'Organization')}</th>
                    <th>{t('shared.criticality', 'Criticality')}</th>
                    <th>{t('shared.manufacturer', 'Manufacturer')}</th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {assets.map((asset, idx) => (
                      <motion.tr
                        key={asset.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.2 }}
                      >
                        <th scope="row">{idx + 1}</th>
                        <td style={{ maxWidth: '220px' }}>
                          <span
                            className="text-truncate d-inline-block w-100"
                            title={asset.assetName}
                          >
                            {asset.assetName}
                          </span>
                        </td>
                        <td className="text-muted">{asset.assetNumber || '-'}</td>
                        <td>
                          {asset.assetStatus ? (
                            <span
                              className={`badge ${getAssetStatusBadgeClass(
                                asset.assetStatus
                              )}`}
                            >
                              {t(
                                `shared.${asset.assetStatus.toLowerCase()}`,
                                asset.assetStatus
                              ).toUpperCase()}
                            </span>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>
                        <td>{asset.subOrgName || '-'}</td>
                        <td>
                          {asset.criticality
                            ? t(
                                `shared.${asset.criticality.toLowerCase()}`,
                                asset.criticality.charAt(0).toUpperCase() +
                                  asset.criticality.slice(1).toLowerCase()
                              )
                            : '-'}
                        </td>
                        <td>{asset.manufacturer || '-'}</td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                  {!loading && assets.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center text-muted py-4">
                        —
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-center mt-3">
              {loading && <Spinner color="primary" />}
              {!loading && hasMore && (
                <Button color="primary" onClick={handleLoadMore}>
                  {t('shared.global_org.load_more', 'Load More')}
                </Button>
              )}
              {!loading && !hasMore && assets.length > 0 && (
                <small className="text-muted">
                  {t('shared.global_org.no_more', 'No more items to load')}
                </small>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default GlobalAssets;
